import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock, Search, CheckCircle, Mail, Plus, Check, Loader, ArrowRight,
} from 'lucide-react';
import Layout from '../../components/layout/Layout';
import {
  SOLAR_IMAGES, WIND_IMAGES, ENERGY_IMAGES,
  PROFESSIONAL_IMAGES, IMAGE_SETS, ALL_IMAGES
} from '../../constants/images';
import backendApi from '../../services/backendApi';
import Reveal from '../../components/enterprise/Reveal';
import '../../styles/enterprise.css';

const NEWS_LOOKBACK_DAYS = 3;

const parseArticleDate = (value) => {
  if (!value) return null;

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  return null;
};

const normalizeSourceImage = (value) => {
  const image = String(value || '').trim();
  if (!image) return '';
  if (image.startsWith('//')) return `https:${image}`;
  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:image/')) {
    return image;
  }
  return '';
};

const normalizeNewsItems = (articles) => {
  const now = Date.now();
  const cutoff = now - (NEWS_LOOKBACK_DAYS * 24 * 60 * 60 * 1000);

  const normalized = (Array.isArray(articles) ? articles : [])
    .map((article, index) => {
      const publishedAt = parseArticleDate(article?.published_date);
      return {
        ...article,
        id: article?.id || article?.url || `${article?.title || 'article'}-${index}`,
        image_url: normalizeSourceImage(
          article?.image_url ||
          article?.imageUrl ||
          article?.thumbnail_url ||
          article?.thumbnailUrl ||
          article?.thumbnail ||
          article?.image
        ),
        published_date: publishedAt ? publishedAt.toISOString() : article?.published_date || '',
        publishedAtMs: publishedAt ? publishedAt.getTime() : 0,
      };
    })
    .filter((article) => article.title && article.url)
    .sort((left, right) => right.publishedAtMs - left.publishedAtMs);

  const recentArticles = normalized.filter((article) => article.publishedAtMs >= cutoff);
  return recentArticles.length > 0 ? recentArticles : normalized;
};

const DEFAULT_PUBLISHERS = [
  { name: 'Mercom India', code: 'Mercom India', iconText: 'MI' },
  { name: 'Business Standard', code: 'Business Standard', iconText: 'BS' },
  { name: 'PV Magazine', code: 'PV Magazine', iconText: 'PV' },
  { name: 'Reuters', code: 'Reuters', iconText: 'RE' },
  { name: 'Economic Times', code: 'Economic Times', iconText: 'ET' },
  { name: 'CERC News', code: 'CERC News', iconText: 'CE' },
  { name: 'IEEE Spectrum', code: 'IEEE Spectrum', iconText: 'IE' },
  { name: 'LiveMint', code: 'LiveMint', iconText: 'LM' },
];

const NewsPage = () => {
  const navigate = useNavigate();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPublishers, setSelectedPublishers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [emailSubscribed, setEmailSubscribed] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  // Stock quotes state
  const [stockQuotes, setStockQuotes] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(true);

  useEffect(() => {
    document.body.classList.add('ibm-plex-page');
    return () => {
      document.body.classList.remove('ibm-plex-page');
    };
  }, []);

  // Fetch news from Django GNews cache endpoint with auto-retry if initial response is empty
  useEffect(() => {
    let isMounted = true;
    let retryTimer = null;
    let retryCount = 0;
    const maxRetries = 4;

    const fetchNews = async (isRetry = false) => {
      try {
        if (!isRetry) setLoading(true);
        const response = await backendApi.getNewsTicker();
        if (response && response.success && Array.isArray(response.data) && response.data.length > 0) {
          if (isMounted) {
            setNewsItems(normalizeNewsItems(response.data));
            setLoading(false);
          }
        } else {
          // If empty (scraping in progress), retry after 1.5 seconds
          if (retryCount < maxRetries) {
            retryCount += 1;
            retryTimer = setTimeout(() => {
              if (isMounted) fetchNews(true);
            }, 1500);
          } else {
            if (isMounted) {
              setNewsItems([]);
              setLoading(false);
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch news ticker items:", err);
        if (retryCount < maxRetries) {
          retryCount += 1;
          retryTimer = setTimeout(() => {
            if (isMounted) fetchNews(true);
          }, 1500);
        } else {
          if (isMounted) {
            setNewsItems([]);
            setLoading(false);
          }
        }
      }
    };

    fetchNews();

    return () => {
      isMounted = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  // Fetch real-time stock quotes
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        setStocksLoading(true);
        const response = await backendApi.getStockQuotes();
        if (response.success && response.data.length > 0) {
          setStockQuotes(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch stock quotes:', err);
      } finally {
        setStocksLoading(false);
      }
    };
    fetchStocks();
    // Refresh every 5 minutes
    const interval = setInterval(fetchStocks, 300000);
    return () => clearInterval(interval);
  }, []);

  // Suggested Topics / Categories (aligned with views.py scraping)
  const topics = [
    { id: 'all', name: 'All News' },
    { id: 'Competitor News', name: 'Competitor News' },
    { id: 'Financial News', name: 'Financial News' },
    { id: 'Regulatory News', name: 'Regulatory News' },
    { id: 'Compliance News', name: 'Compliance News' },
    { id: 'Renewable Updates', name: 'Renewable Updates' },
    { id: 'Engineering & Technology News', name: 'Engineering & Tech' },
    { id: 'Supply Chain News', name: 'Supply Chain' },
    { id: 'Renewable Power', name: 'Renewable Power' },
    { id: 'Data Science', name: 'Data Science' },
    { id: 'Emerging Technology', name: 'Emerging Tech' }
  ];

  // Suggested Publishers — derived dynamically from actual scraped newsItems or fallback
  const publishers = React.useMemo(() => {
    const seen = new Map();
    newsItems.forEach(article => {
      const name = (article.publisher || '').trim();
      if (name && !seen.has(name)) {
        const words = name.split(/\s+/);
        const code = words.length >= 2
          ? (words[0][0] + words[1][0]).toUpperCase()
          : name.slice(0, 2).toUpperCase();
        seen.set(name, { name, code: name, iconText: code });
      }
    });
    const parsed = Array.from(seen.values()).slice(0, 8);
    return parsed.length > 0 ? parsed : DEFAULT_PUBLISHERS;
  }, [newsItems]);

  const toggleSelection = (value, setSelectedValues) => {
    setSelectedValues((prev) => (
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    ));
  };

  // Stable image mapper based on the title string hash
  const getArticleImage = (title, category) => {
    let hash = 0;
    const cleanTitle = title || "";
    for (let i = 0; i < cleanTitle.length; i++) {
      hash = cleanTitle.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);

    let images = ALL_IMAGES;
    const cat = String(category || '').toLowerCase();
    if (cat.includes('solar') || cat.includes('renew') || cat.includes('power')) {
      images = SOLAR_IMAGES;
    } else if (cat.includes('wind')) {
      images = WIND_IMAGES;
    } else if (cat.includes('tech') || cat.includes('data') || cat.includes('ai') || cat.includes('science')) {
      images = [IMAGE_SETS.news.technology, ...ENERGY_IMAGES];
    } else if (cat.includes('competitor') || cat.includes('financial') || cat.includes('regulatory') || cat.includes('compliance')) {
      images = PROFESSIONAL_IMAGES;
    }

    return images[hash % images.length];
  };

  const resolveArticleImage = (article) => {
    const sourceImage = normalizeSourceImage(
      article?.image_url ||
      article?.imageUrl ||
      article?.thumbnail_url ||
      article?.thumbnailUrl ||
      article?.thumbnail ||
      article?.image
    );

    return sourceImage || getArticleImage(article?.title, article?.category);
  };

  // Click handler that supports both redirecting external sources & internal relative links
  const handleCardClick = (url) => {
    if (!url) return;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  // Time formatting helper
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '2h ago';
    try {
      const pubDate = new Date(dateStr);
      const now = new Date();
      const diffMs = now - pubDate;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60 && diffMins > 0) return `${diffMins}m ago`;
      if (diffHours < 24 && diffHours > 0) return `${diffHours}h ago`;
      if (diffDays < 7 && diffDays > 0) return `${diffDays}d ago`;
      return pubDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch (e) {
      return '2h ago';
    }
  };

  // Filtering news items
  const filteredArticles = newsItems.filter(article => {
    const matchesCategory =
      selectedCategories.length === 0 ||
      selectedCategories.includes(article.category);

    const publisherValue = (article.publisher || '').toLowerCase();
    const matchesPublisher =
      selectedPublishers.length === 0 ||
      selectedPublishers.some((publisher) => publisherValue.includes(publisher.toLowerCase()));

    const matchesSearch = !searchTerm ||
      article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.category?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesPublisher && matchesSearch;
  });

  const leadArticle = filteredArticles[0];
  const secondaryArticles = filteredArticles.slice(1, 4);
  const feedArticles = filteredArticles.slice(4);

  const activeFilterCount = selectedCategories.length + selectedPublishers.length + (searchTerm ? 1 : 0);

  const resetFilters = () => {
    setSelectedCategories([]);
    setSelectedPublishers([]);
    setSearchTerm('');
  };

  const readingTime = (article) => `${(article.title.length % 3) + 3} min read`;

  const ArticleMeta = ({ article, withReadTime }) => (
    <div className="ax-meta">
      <span className="ax-meta__source">{article.publisher || 'Axon News'}</span>
      <span className="ax-meta__dot" />
      <span>{formatTimeAgo(article.published_date)}</span>
      {withReadTime && (
        <>
          <span className="ax-meta__dot" />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Clock size={13} />
            {readingTime(article)}
          </span>
        </>
      )}
    </div>
  );

  const ArticleImage = ({ article }) => (
    <img
      src={resolveArticleImage(article)}
      alt={article.title}
      loading="lazy"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = getArticleImage(article?.title, article?.category);
      }}
    />
  );

  return (
    <Layout header={6} footer={6}>
      <main className="ax">
        {/* --- Newsroom hero ---------------------------------------------- */}
        <section className="ax-hero">
          <div className="ax-hero__inner" style={{ paddingBlock: 'clamp(80px, 8vw, 128px)' }}>
            <div className="ax-container">
              <div className="ax-hero__copy">
                <Reveal>
                  <span className="ax-eyebrow">Newsroom</span>
                </Reveal>
                <Reveal delay={60}>
                  <h1 className="ax-display">News</h1>
                </Reveal>
                <Reveal delay={120}>
                  <p className="ax-hero__lede">
                    Latest updates, insights and announcements from across the renewable
                    energy sector — curated for operators, analysts and asset owners.
                  </p>
                </Reveal>
                <Reveal delay={180} style={{ marginTop: 36, maxWidth: 480 }}>
                  <div className="ax-search">
                    <Search />
                    <input
                      type="search"
                      aria-label="Search articles"
                      placeholder="Search articles, topics or publishers"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* --- Topic filters ----------------------------------------------- */}
        <div className="ax-container">
          <div className="ax-filterbar">
            <span className="ax-label" style={{ marginRight: 8 }}>Topics</span>
            {topics.filter((t) => t.id !== 'all').map((topic) => {
              const isActive = selectedCategories.includes(topic.id);
              return (
                <button
                  key={topic.id}
                  type="button"
                  className="ax-chip"
                  aria-pressed={isActive}
                  onClick={() => toggleSelection(topic.id, setSelectedCategories)}
                >
                  {topic.name}
                </button>
              );
            })}
            {activeFilterCount > 0 && (
              <button
                type="button"
                className="ax-textlink"
                style={{ marginLeft: 'auto' }}
                onClick={resetFilters}
              >
                Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>

        {/* --- Stories ----------------------------------------------------- */}
        <section className="ax-section ax-section--tight" style={{ paddingTop: 0 }}>
          <div className="ax-container">
            <div className="ax-news-layout">
              <div>
                {loading && newsItems.length === 0 ? (
                  <div style={{ display: 'grid', gap: 28, paddingTop: 48 }}>
                    <div className="ax-skeleton" style={{ height: 320 }} />
                    <div className="ax-grid ax-grid--3 ax-grid--gap">
                      <div className="ax-skeleton" style={{ height: 220 }} />
                      <div className="ax-skeleton" style={{ height: 220 }} />
                      <div className="ax-skeleton" style={{ height: 220 }} />
                    </div>
                  </div>
                ) : filteredArticles.length === 0 ? (
                  <div style={{ paddingBlock: 96, textAlign: 'center' }}>
                    <h2 className="ax-h3" style={{ marginBottom: 12 }}>No matching articles</h2>
                    <p className="ax-body" style={{ marginBottom: 28 }}>
                      Try removing a topic, publisher or search term.
                    </p>
                    <button type="button" className="ax-btn ax-btn--secondary" onClick={resetFilters}>
                      Reset filters
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Featured story */}
                    {leadArticle && (
                      <Reveal
                        as="button"
                        className="ax-lead"
                        type="button"
                        onClick={() => handleCardClick(leadArticle.url)}
                      >
                        <figure className="ax-media">
                          <ArticleImage article={leadArticle} />
                        </figure>
                        <div>
                          <span className="ax-tag" style={{ marginBottom: 16 }}>
                            {leadArticle.category || 'Featured'}
                          </span>
                          <h2 className="ax-lead__title">{leadArticle.title}</h2>
                          {leadArticle.description && (
                            <p className="ax-body ax-clamp-3" style={{ marginBottom: 18 }}>
                              {leadArticle.description}
                            </p>
                          )}
                          <ArticleMeta article={leadArticle} withReadTime />
                        </div>
                      </Reveal>
                    )}

                    {/* Secondary stories */}
                    {secondaryArticles.length > 0 && (
                      <div
                        className="ax-grid ax-grid--3 ax-grid--gap"
                        style={{ paddingBlock: 'clamp(32px, 4vw, 56px)', borderBottom: '1px solid var(--ax-border)' }}
                      >
                        {secondaryArticles.map((article, idx) => (
                          <Reveal
                            as="button"
                            type="button"
                            key={article.id}
                            className="ax-story"
                            delay={idx * 70}
                            onClick={() => handleCardClick(article.url)}
                          >
                            <figure className="ax-media">
                              <ArticleImage article={article} />
                            </figure>
                            <ArticleMeta article={article} />
                            <h3 className="ax-story__title ax-clamp-3">{article.title}</h3>
                          </Reveal>
                        ))}
                      </div>
                    )}

                    {/* Feed */}
                    {feedArticles.length > 0 && (
                      <div style={{ paddingTop: 24 }}>
                        <h2 className="ax-label" style={{ marginBottom: 8 }}>More stories</h2>
                        {feedArticles.map((article, idx) => (
                          <Reveal
                            as="button"
                            type="button"
                            key={article.id}
                            className="ax-row"
                            delay={Math.min(idx, 4) * 50}
                            onClick={() => handleCardClick(article.url)}
                          >
                            <div>
                              <ArticleMeta article={article} withReadTime />
                              <h3 className="ax-row__title ax-clamp-2">{article.title}</h3>
                              {article.description && (
                                <p className="ax-body ax-clamp-2">{article.description}</p>
                              )}
                            </div>
                            <figure className="ax-media">
                              <ArticleImage article={article} />
                            </figure>
                          </Reveal>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* --- Rail ------------------------------------------------- */}
              <aside className="ax-rail" style={{ paddingTop: 48 }}>
                <div className="ax-panel">
                  <h2 className="ax-panel__title">Publishers</h2>
                  <div className="ax-sourcelist">
                    {publishers.map((pub) => {
                      const isActive = selectedPublishers.includes(pub.code);
                      return (
                        <button
                          key={pub.code}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() => toggleSelection(pub.code, setSelectedPublishers)}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                            <span className="ax-sourcelist__mark">{pub.iconText}</span>
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {pub.name}
                            </span>
                          </span>
                          <span className="ax-sourcelist__state">
                            {isActive ? <Check size={16} /> : <Plus size={16} />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="ax-panel ax-panel--ink">
                  <h2 className="ax-panel__title">
                    <Mail size={13} style={{ display: 'inline', marginRight: 7, verticalAlign: '-2px' }} />
                    Newsletter
                  </h2>
                  <h3 className="ax-h3" style={{ color: '#fff', marginBottom: 10 }}>
                    Get the top stories in your email
                  </h3>
                  <p className="ax-body" style={{ color: 'rgba(255,255,255,0.74)', marginBottom: 22 }}>
                    Sign up for the Axon News daily digest and stay ahead of competitor updates.
                  </p>

                  {emailSubscribed ? (
                    <div
                      style={{
                        display: 'flex',
                        gap: 12,
                        padding: 16,
                        border: '1px solid rgba(255,255,255,0.24)',
                        borderRadius: 'var(--ax-radius)',
                      }}
                    >
                      <CheckCircle size={18} style={{ flex: 'none', color: 'var(--ax-blue-300)' }} />
                      <div>
                        <p style={{ color: '#fff', fontSize: '0.875rem', fontWeight: 600 }}>
                          Subscribed successfully
                        </p>
                        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', marginTop: 4 }}>
                          You will receive the Axon News digest in your inbox.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const email = emailInput.trim();
                        if (!email) return;
                        setSubscribeLoading(true);
                        setSubscribeError('');
                        try {
                          const res = await backendApi.makeRequest('/v1/newsletter/subscribe/', {
                            method: 'POST',
                            body: JSON.stringify({ email }),
                            headers: { 'Content-Type': 'application/json' },
                          });
                          if (res && res.success) {
                            setEmailSubscribed(true);
                          } else {
                            setSubscribeError(res?.error || 'Subscription failed. Please try again.');
                          }
                        } catch (err) {
                          setSubscribeError('Could not connect to server. Please try again.');
                        } finally {
                          setSubscribeLoading(false);
                        }
                      }}
                      style={{ display: 'grid', gap: 14 }}
                    >
                      <div className="ax-field">
                        <label htmlFor="newsletter-email">Work email</label>
                        <input
                          id="newsletter-email"
                          type="email"
                          required
                          placeholder="you@company.com"
                          value={emailInput}
                          onChange={(e) => { setEmailInput(e.target.value); setSubscribeError(''); }}
                          disabled={subscribeLoading}
                        />
                        {subscribeError && (
                          <p className="ax-field__error" style={{ color: '#FFB4AB' }}>{subscribeError}</p>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={subscribeLoading}
                        className="ax-btn ax-btn--invert ax-btn--block"
                      >
                        {subscribeLoading ? (
                          <>
                            <Loader size={16} className="ax-spin" /> Subscribing…
                          </>
                        ) : (
                          <>
                            <span>Subscribe</span>
                            <ArrowRight className="ax-btn__arrow" size={17} />
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default NewsPage;
